import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoWalk } from "react-icons/io5";
import Quote from "../../../../../../assets/icons/Quote.png";
import UserIcon from "../../../../../../assets/icons/UserIcon.png";
import ChatBubble from "../../../../../../assets/icons/chatBubble.svg";
import ExternalLink from "../../../../../../assets/icons/external-link.svg";
import Eye from "../../../../../../assets/icons/eye.svg";
import More from "../../../../../../assets/icons/more.svg";
import { useAppContext } from "../../../../../../hooks/useAppContext";
import { useDiaryContext } from "../../../../../../hooks/useDiaryContext";
import {
  BookingStage,
  BookingType,
  BookingZone,
  VenueConfig
} from "../../../../../../types/venues";
import {
  calculateDesktopDragOffset,
  calculateTouchDragOffset,
  cleanupDragClones,
  createDragClone
} from "../../../../../../utils/dragCloneUtils";

import { ViewBooking, ViewBookingStatusEnum } from "collins-js-sdk";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, GripVertical, LogOut } from "lucide-react";
import {
  BookingTag,
  VenueGroupConfig
} from "../../../../../../types/VenueGroup";
import { CustomFieldTag } from "../../../../../../types/bookings";
import {
  getBookingStageFromKey,
  getCustomerName,
  getNextBookingStageFromKey,
  isSameCollinsDay,
  walkingBookings
} from "../../../../../../utils/booking";
import {
  filterCompletedCustomFields,
  formatFieldValue
} from "../../../../../../utils/customFields";
import {
  calculateDynamicHeight,
  getActivePreorderCount,
  getBookingAuthTotal,
  getBookingCardStyles,
  getBookingPaymentTotal
} from "../../../../../../utils/runsheetUtils";
import { getBookingAssignedAreaNamesAsString } from "../../../../../../utils/singleBooking";
import {
  breakTableNames,
  breakZoneNames
} from "../../../../../../utils/syllableBreaker";
import CustomPill from "../../../../../Shared/CustomPill/CustomPill";
import { EDSSwipeout, EDSSwipeoutOption } from "../../../../../eds/EDSSwipeout";
import { PreviewBookingModal } from "../../../Runsheet/PreviewBookingModal/PreviewBookingModal";
import EditBooking from "../../../SelectedBooking/EditBooking/EditBooking";
import { getCheckInWaitlistProps } from "../../../SelectedBooking/Manage/SelectedBookingManageController";
import SendSms from "../../../SelectedBooking/Messages/SMS/SendSms";
import { Divider } from "../Divider";
import { BookingLabels } from "./BookingLabels";
import { PillsWithOverflow } from "./PillsWithOverflow";

const cardPositionMap = new Map<
  string,
  { top: number; height: number; type: "divider" | "card"; id?: string }[]
>();

type DeviceLayoutConfig = {
  baseOffset: number;
  buffer: number;
};

/**
 * Determines device type and returns appropriate layout configuration
 * for card positioning calculations
 */
const getDeviceLayoutConfig = (): DeviceLayoutConfig => {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  const isMobile = screenWidth <= 480;
  const isTablet = screenWidth > 480 && screenWidth <= 1180;
  const isTabletLandscape = isTablet && screenHeight <= 820;
  const isIPadPro =
    (screenWidth >= 1024 && screenWidth <= 1366) ||
    (screenHeight >= 1024 && screenHeight <= 1366);
  const isLargeResponsive = screenWidth >= 1636;

  if (isMobile) {
    return { baseOffset: 15, buffer: 3 };
  }
  if (isIPadPro) {
    return { baseOffset: 55, buffer: 8 };
  }
  if (isTabletLandscape) {
    return { baseOffset: 35, buffer: 7 };
  }
  if (isTablet) {
    return { baseOffset: 30, buffer: 6 };
  }
  if (isLargeResponsive) {
    return { baseOffset: 0, buffer: 2 };
  }

  return { baseOffset: 20, buffer: 4 };
};

type CardPosition = {
  top: number;
  height: number;
  type: "divider" | "card";
  id?: string;
};

/**
 * Calculates the offset needed to prevent card overlap
 */
const calculateCardOffset = (
  existingPositions: CardPosition[],
  currentTop: number,
  cardHeight: number,
  buffer: number,
  baseOffset: number,
  isFirstCardAfterDivider: boolean
): number => {
  if (isFirstCardAfterDivider) {
    return 0;
  }

  let offset = 0;
  const cardsWithSameTime = existingPositions.filter(
    (pos) => pos.type === "card"
  );
  const hasCardsWithSameTime = cardsWithSameTime.length > 0;

  for (const pos of existingPositions) {
    const posBottom = pos.top + pos.height;
    const currentBottom = currentTop + cardHeight;

    if (currentTop < posBottom && currentBottom > pos.top) {
      const requiredOffset = posBottom - currentTop + buffer;
      offset = Math.max(offset, requiredOffset);
    }
  }

  if (!hasCardsWithSameTime) {
    offset = Math.max(offset, baseOffset);
  }

  return offset;
};

type DiaryBookingCardProps = {
  index: number;
  style: React.CSSProperties;
  parent: {
    props: {
      bookings: any[];
      venueConfig: any;
      venueGroupConfig: any;
      onBookingChanged: (booking: any) => void;
      selectedView: string;
      selectedBooking: any;
      setSelectedBooking: (booking: any) => void;
      refreshBookings: () => void;
      isOnRunSheet: boolean;
      disableSwipe: boolean;
      setDisableSwipe: (disable: boolean) => void;
    };
  };
  refreshBookings?: () => void;
};

// Convert MobileSwipeoutOption to EDSSwipeoutOption
const convertToEDSOptions = (
  options: Array<{
    name: string;
    icon: React.ReactNode;
    label: string;
    accent: string;
  }>
): EDSSwipeoutOption[] => {
  return options.map((option) => ({
    name: option.name,
    icon: option.icon,
    label: option.label,
    accent:
      option.accent === "teal" ||
      option.accent === "blue" ||
      option.accent === "red" ||
      option.accent === "green"
        ? (option.accent as "teal" | "blue" | "red" | "green")
        : "teal" // Default to teal if accent is not one of the expected values
  }));
};

export const RunSheetCard = ({
  index,
  style,
  parent
}: DiaryBookingCardProps) => {
  const { venueGroupConfig: contextVenueGroupConfig } = useAppContext();
  const diaryContext = useDiaryContext();
  const [showSendSms, setShowSendSms] = useState<boolean>(false);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [showEditBooking, setShowEditBooking] = useState<boolean>(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [, setPositionOffset] = useState<number>(0);

  const getTagLabel = (tagId: string, venueGroupConfig: any): string => {
    const tagConfig = venueGroupConfig?.booking_tags?.find(
      (tag: BookingTag) => tag.id === tagId
    );
    return tagConfig?.label || tagId;
  };

  const item: any = parent.props.bookings[index];
  const booking: any = item;

  // Memoize filtered tags to avoid recalculating on every render
  const filteredTags = useMemo(() => {
    if (!booking.tags || !parent.props.venueGroupConfig?.booking_tags) {
      return [];
    }
    return filterCompletedCustomFields(
      booking.tags,
      parent.props.venueGroupConfig.booking_tags
    );
  }, [booking.tags, parent.props.venueGroupConfig?.booking_tags]);

  useEffect(() => {
    const handleResize = () => {
      const timeSlotKey = `${item.time || booking?.time}_${booking?.date || ""}`;
      if (cardPositionMap.has(timeSlotKey)) {
        cardPositionMap.delete(timeSlotKey);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [item.time, booking?.time, booking?.date]);

  useEffect(() => {
    const timeSlotKey = `${item.time || booking?.time}_${booking?.date || ""}`;

    if (item.isDivider) {
      const existingPositions = cardPositionMap.get(timeSlotKey) || [];
      const currentTop = parseFloat(String(style.top || "0"));
      const dividerHeight = 36; // Fixed divider height

      existingPositions.push({
        top: currentTop,
        height: dividerHeight,
        type: "divider"
      });
      cardPositionMap.set(timeSlotKey, existingPositions);

      return () => {
        const positions = cardPositionMap.get(timeSlotKey) || [];
        const updatedPositions = positions.filter(
          (pos) => !(pos.type === "divider" && pos.top === currentTop)
        );
        if (updatedPositions.length === 0) {
          cardPositionMap.delete(timeSlotKey);
        } else {
          cardPositionMap.set(timeSlotKey, updatedPositions);
        }
      };
    }

    if (!booking?.time || !booking?.id) return;

    const existingPositions = cardPositionMap.get(timeSlotKey) || [];
    const currentTop = parseFloat(String(style.top || "0"));
    const cardHeight = calculateDynamicHeight(booking, true);

    const { baseOffset, buffer } = getDeviceLayoutConfig();

    const previousItem = parent.props.bookings[index - 1];
    const isFirstCardAfterDivider =
      index === 0 || previousItem?.isDivider === true;

    const offset = calculateCardOffset(
      existingPositions,
      currentTop,
      cardHeight,
      buffer,
      baseOffset,
      isFirstCardAfterDivider
    );

    existingPositions.push({
      top: currentTop + offset,
      height: cardHeight,
      type: "card",
      id: booking.id
    });
    cardPositionMap.set(timeSlotKey, existingPositions);

    setPositionOffset(offset);

    return () => {
      const positions = cardPositionMap.get(timeSlotKey) || [];
      const updatedPositions = positions.filter(
        (pos) => !(pos.type === "card" && pos.id === booking.id)
      );
      if (updatedPositions.length === 0) {
        cardPositionMap.delete(timeSlotKey);
      } else {
        cardPositionMap.set(timeSlotKey, updatedPositions);
      }
    };
  }, [
    booking?.time,
    booking?.date,
    booking?.id,
    style.top,
    item.isDivider,
    index,
    parent.props.bookings
  ]);

  if (item.isDivider) {
    return (
      <Divider
        key={`divider-${item.time}-${index}`}
        style={style}
        item={item}
        isOnRunSheet={true}
      />
    );
  }

  const selectedView = parent.props.selectedView;
  const venueConfig: VenueConfig = parent.props.venueConfig;
  const venueGroupConfig: VenueGroupConfig = parent.props.venueGroupConfig;
  const onBookingChanged: any = parent.props.onBookingChanged;
  const selectedBooking: ViewBooking | undefined = parent.props.selectedBooking;
  const setSelectedBooking: React.Dispatch<
    React.SetStateAction<ViewBooking | undefined>
  > = parent.props.setSelectedBooking;
  const refreshBookings = parent.props.refreshBookings;

  const selected: boolean = booking?.id === selectedBooking?.id;

  const currentStage: BookingStage | null = getBookingStageFromKey(
    booking.currentStage,
    venueConfig,
    venueGroupConfig || contextVenueGroupConfig
  );

  const currentStageWalkIn: BookingStage | null = isSameCollinsDay(
    new Date(),
    new Date(booking.date)
  )
    ? walkingBookings(
        booking.currentStage,
        venueConfig,
        venueGroupConfig || contextVenueGroupConfig
      )
    : currentStage;

  const nextStage: BookingStage | null = getNextBookingStageFromKey(
    currentStage ? currentStage.id : "",
    venueConfig,
    venueGroupConfig || contextVenueGroupConfig
  );

  const nextStageWalkIn: BookingStage | null = getNextBookingStageFromKey(
    currentStageWalkIn ? currentStageWalkIn.id : "",
    venueConfig,
    venueGroupConfig || contextVenueGroupConfig
  );

  // Helper function to check if booking can have swipeout options
  const canShowSwipeoutOptions = (): boolean => {
    if (!booking.id) return false;
    if (booking.status === ViewBookingStatusEnum.Rejected) return false;
    if (!isSameCollinsDay(new Date(), new Date(booking.date))) return false;
    if (currentStage?.id === "left") return false;
    if (currentStageWalkIn?.id === "left") return false;
    return true;
  };

  // Helper function to create stage progression option
  const createStageProgressionOption = (
    stage: BookingStage | null
  ): {
    name: string;
    icon: React.ReactNode;
    label: string;
    accent: string;
  } | null => {
    if (!stage) return null;
    return {
      name: stage.id,
      icon: <ArrowRight size={16} style={{ color: "var(--primaryColor)" }} />,
      label: stage.label,
      accent: "teal"
    };
  };

  // Helper function to get left swipeout options
  const getLeftOptions = (): Array<{
    name: string;
    icon: React.ReactNode;
    label: string;
    accent: string;
  }> => {
    if (!canShowSwipeoutOptions()) return [];
    if (!nextStageWalkIn || !nextStage) return [];

    const option = booking.walkIn
      ? createStageProgressionOption(nextStageWalkIn)
      : createStageProgressionOption(nextStage);

    return option ? [option] : [];
  };

  // Helper function to get right swipeout options
  const getRightOptions = (): Array<{
    name: string;
    icon: React.ReactNode;
    label: string;
    accent: string;
  }> => {
    if (!canShowSwipeoutOptions()) return [];

    return [
      {
        icon: <LogOut size={16} style={{ color: "var(--primaryColor)" }} />,
        name: "left",
        label: "Left",
        accent: "blue"
      }
    ];
  };

  const leftOptions = getLeftOptions();
  const rightOptions = getRightOptions();

  // Helper function to process zone names
  const getZoneNamesData = (
    booking: ViewBooking,
    venueConfig: VenueConfig
  ): {
    zoneNamesAsStringRecord: string[];
    zoneNamesAsStringMultyRecords: string;
  } => {
    const zoneIds = booking.assignedAreas
      ? booking.assignedAreas
          .map((area) => area.zone?.toString())
          .filter((zone): zone is string => !!zone)
      : [];

    const zoneNames = zoneIds
      .map((zoneId: string) => {
        const zone: BookingZone | undefined = (
          venueConfig?.bookable_area_zones ?? []
        ).find(({ id }: BookingZone) => id === zoneId);

        return zone?.name ?? "";
      })
      .filter((zoneName): zoneName is string => !!zoneName);

    const zoneNamesAsStringRecord = zoneNames.filter(function (
      val: string,
      id: number,
      array: Array<string>
    ) {
      return array.indexOf(val) === id;
    });
    const zoneNamesAsStringMultyRecords =
      zoneNamesAsStringRecord.length > 1
        ? zoneNamesAsStringRecord
            .slice(0, -1)
            .join(", ")
            .concat(" & ", zoneNamesAsStringRecord.slice(-1)[0])
        : (zoneNamesAsStringRecord[0] ?? "");

    return { zoneNamesAsStringRecord, zoneNamesAsStringMultyRecords };
  };

  const { zoneNamesAsStringRecord, zoneNamesAsStringMultyRecords } =
    getZoneNamesData(booking, venueConfig);

  const bookingAssignedAreasAsString =
    getBookingAssignedAreaNamesAsString(booking);

  // Helper function to get booking type
  const getBookingType = (
    booking: ViewBooking,
    venueConfig: VenueConfig
  ): BookingType | null => {
    if (!venueConfig.booking_types || !booking.type) {
      return null;
    }
    return (
      venueConfig.booking_types.find(
        (bookingType: BookingType) => bookingType.id === booking.type?.id
      ) ?? null
    );
  };

  const bookingType = getBookingType(booking, venueConfig);
  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;
  // Helper function to handle booking update and refresh
  const updateBookingAndRefresh = (updatedBooking: ViewBooking): void => {
    onBookingChanged(updatedBooking);
    if (typeof refreshBookings === "function") {
      refreshBookings();
    }
  };

  // Helper function to handle no-show action
  const handleNoShow = (booking: ViewBooking): void => {
    const updatedBooking = {
      ...booking,
      actualGuests: 0,
      currentStage: "no-show"
    };
    updateBookingAndRefresh(updatedBooking);
  };

  // Helper function to handle left action
  const handleLeft = (booking: ViewBooking): void => {
    const updatedBooking = { ...booking, currentStage: "left" };
    updateBookingAndRefresh(updatedBooking);
  };

  // Helper function to get target stage for progression
  const getTargetStageForProgression = (): string | null => {
    const nextStageForProgression = getNextBookingStageFromKey(
      currentStage ? currentStage.id : "",
      venueConfig,
      venueGroupConfig || contextVenueGroupConfig
    );

    const nextStageWalkInForProgression: BookingStage | null =
      getNextBookingStageFromKey(
        currentStageWalkIn ? currentStageWalkIn.id : "",
        venueConfig,
        venueGroupConfig || contextVenueGroupConfig
      );

    if (booking.walkIn) {
      return nextStageWalkInForProgression?.id ?? null;
    }
    return nextStageForProgression?.id ?? null;
  };

  // Helper function to handle stage progression
  const handleStageProgression = (booking: ViewBooking): void => {
    const targetStage = getTargetStageForProgression();
    const checkInWaitlistProps = booking?.currentStage
      ? {}
      : getCheckInWaitlistProps(booking);

    const updatedBooking = {
      ...booking,
      currentStage: targetStage,
      ...checkInWaitlistProps
    };
    updateBookingAndRefresh(updatedBooking);
  };

  const swipeoutOptionClick = (name: string, booking: ViewBooking): void => {
    if (name === "no-show") {
      handleNoShow(booking);
      return;
    }

    if (name === "left") {
      handleLeft(booking);
      return;
    }

    handleStageProgression(booking);
  };

  return (
    <>
      {showSendSms && (
        <SendSms
          handleClose={() => setShowSendSms(false)}
          handleSent={() => {
            setShowSendSms(false);
          }}
        />
      )}
      {showEditBooking && (
        <EditBooking handleClose={() => setShowEditBooking(false)} />
      )}
      {showPreviewModal && (
        <PreviewBookingModal
          booking={booking}
          venueConfig={venueConfig}
          venueGroupConfig={contextVenueGroupConfig}
          onClose={() => setShowPreviewModal(false)}
          onEditBooking={() => {
            setShowPreviewModal(false);
            setShowEditBooking(true);
          }}
        />
      )}
      <motion.div
        ref={cardRef}
        initial="hidden"
        animate="visible"
        transition={{
          duration: window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches
            ? 0
            : 0.2
        }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 }
        }}
        key={booking.id || booking.offlineId || index}
        style={{
          ...style,
          top: `${parseFloat(String(style.top || "0"))}px`,
          display: "flex",
          width: "var(--fullWidth)",
          minHeight: "160px",
          height: "auto"
        }}
        className={`runsheet-card-div-${booking.id}`}
      >
        <EDSSwipeout
          tabIndex={0}
          leftOptions={convertToEDSOptions(leftOptions)}
          rightOptions={convertToEDSOptions(rightOptions)}
          onOptionClick={(e: string) => swipeoutOptionClick(e, booking)}
          style={{
            width: "100%",
            minHeight: "160px"
          }}
        >
          <div
            id={`Booking_${booking.id}`}
            className={"DiarySidebarListItemGrid DiaryBookingCard__grid"}
            style={getBookingCardStyles(booking, venueConfig)}
            onClick={() => {
              setSelectedBooking(selected ? undefined : booking);
            }}
            onKeyDown={() => {
              setSelectedBooking(selected ? undefined : booking);
            }}
            role="button"
            tabIndex={0}
          >
            {/* Column 1 - Name*/}
            <div className="Column1">
              <div className="Column1StateAndDetails">
                {booking.walkIn ? (
                  <div
                    className={`DiarySidebarListItem__state DiarySidebarListItem__state--${
                      currentStageWalkIn?.label || "awaiting"
                    }`}
                  >
                    <span className="BookingStage_Label">
                      {booking.currentStage &&
                        booking.currentStage.id !== "awaiting" && (
                          <>
                            {currentStageWalkIn?.label?.charAt(0).toUpperCase()}
                          </>
                        )}
                    </span>
                    <div
                      className="BookingStage_Colour"
                      style={{
                        backgroundColor: `#${currentStageWalkIn?.colour ?? ""}`
                      }}
                    ></div>
                  </div>
                ) : (
                  <div
                    className={`DiarySidebarListItem__state DiarySidebarListItem__state--${
                      currentStage?.label || "awaiting"
                    }`}
                  >
                    <span className="BookingStage_Label">
                      {booking.currentStage &&
                        booking.currentStage.id !== "awaiting" && (
                          <>{currentStage?.label.charAt(0).toUpperCase()}</>
                        )}
                    </span>
                    <div
                      className="BookingStage_Colour"
                      style={{
                        backgroundColor: `#${currentStage?.colour ?? ""}`
                      }}
                    ></div>
                  </div>
                )}

                <div className="DiarySidebarListItem__details">
                  <div className="RunsheetCardListRow Row1">
                    <div className="DiarySidebarListItem__detailsName">
                      {booking.walkIn ? (
                        <div className="DiarySidebarListItem_walkIn">
                          <IoWalk /> <span>Walk In</span>
                        </div>
                      ) : (
                        getCustomerName(booking)
                      )}
                    </div>
                  </div>
                  {booking.phone && (
                    <div className="RunsheetCardListRow Row2">
                      {" "}
                      {booking.phone}{" "}
                      <button
                        className="DiarySidebarListItem__TextButton"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            booking.phone &&
                            venueConfig?.booking_sms_enabled
                          ) {
                            setShowSendSms(true);
                          }
                        }}
                      >
                        <img
                          src={ChatBubble}
                          alt="message"
                          style={{
                            width: 20,
                            height: 20
                          }}
                        />
                        Text
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {shouldShowBookingLabels(booking) && (
                <div className="RunsheetCardListRow Row3">
                  <BookingLabels
                    booking={booking}
                    venueConfig={venueConfig}
                    venueGroupConfig={
                      venueGroupConfig || contextVenueGroupConfig
                    }
                    maxPills={4}
                  />
                </div>
              )}
            </div>

            {/* Column 2 - guests */}
            <div className="Column2">
              <div className="RunsheetCardListRow Row3">
                {booking.unsaved ? (
                  <div className="DiarySidebarListItem__detailsOffline">
                    <div className="DiarySidebarListItem__detailsOffline__icon">
                      <AlertTriangle
                        size={16}
                        style={{ color: "var(--accent-color, #1fd4d4)" }}
                      />
                    </div>
                    {!booking.id && <span>Not Updated</span>}
                    {booking.id && (
                      <span>
                        {booking.status === ViewBookingStatusEnum.Rejected ||
                        booking.status === ViewBookingStatusEnum.Deleted
                          ? "Deleted "
                          : "Modified "}
                        - Not Updated
                      </span>
                    )}
                  </div>
                ) : (
                  <>
                    <div
                      className="DiarySidebarListItem__Icons"
                      id={`bookingCard_${booking.id}`}
                      key={`bookingCard_${booking.id}`}
                    >
                      {booking.actualGuests !== undefined &&
                        booking.actualGuests !== null && (
                          <>
                            <img src={UserIcon} alt="icon-user" />
                            <span className="numPeople">
                              {booking.actualGuests}
                            </span>
                          </>
                        )}
                    </div>
                    <DisplayMoreIcon bookingId={`${booking.id}`} />
                  </>
                )}
              </div>
            </div>

            {/* Column 3- Table */}
            <div className="Column3">
              <div className="RunsheetCardListRow Row2">
                <div className="DiarySidebarListItem__detailsTable">
                  <span
                    className="DiarySidebarListItem__detailsTable_View"
                    dangerouslySetInnerHTML={{
                      __html: breakTableNames(bookingAssignedAreasAsString)
                    }}
                  />
                </div>
                <div className="DiarySidebarListItem__auxZone">
                  <span
                    className="DiarySidebarListItem__auxZone_View"
                    dangerouslySetInnerHTML={{
                      __html: breakZoneNames(
                        zoneNamesAsStringRecord.length > 1
                          ? zoneNamesAsStringMultyRecords
                          : zoneNamesAsStringRecord.toString()
                      )
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Column 4 - Type */}
            <div className="Column4">
              {bookingType?.name && (
                <PillsWithOverflow
                  pills={[
                    {
                      key: "booking-type",
                      label: bookingType.name,
                      backgroundColour: bookingType.color ?? undefined,
                      labelColour: bookingType.color ? "white" : undefined,
                      softenColour: true,
                      className: "DiarySidebarListItem__detailsType",
                      style: {
                        "--booking-type-color":
                          bookingType.color ?? "hsl(var(--primary))",
                        "--booking-type-foreground": bookingType.color
                          ? "white"
                          : "hsl(var(--primary-foreground))"
                      } as React.CSSProperties
                    }
                  ]}
                  maxHeight={120}
                />
              )}
            </div>

            {/* Column 5 - Booking Tags and Special Requests */}
            <div className="Column5">
              <div className="DiarySidebarListItem__customerRequestsContainer">
                {/* Booking Tags */}
                {filteredTags.length > 0 && (
                  <>
                    {filteredTags.map((tag: CustomFieldTag) => (
                      <div
                        key={tag.id}
                        className="DiarySidebarListItem__customerRequestsRow"
                      >
                        <span className="DiarySidebarListItem__tagLabel">
                          <strong>
                            {getTagLabel(tag.id, parent.props.venueGroupConfig)}
                          </strong>{" "}
                          : {formatFieldValue(tag)}
                        </span>
                      </div>
                    ))}
                  </>
                )}

                {/* Existing special requests content */}
                {booking.notes && (
                  <div className="DiarySidebarListItem__customerRequestsRow">
                    <img src={Quote} alt="Quote" />
                    <span className="DiarySidebarListItem__customerRequests">
                      {booking.notes}
                    </span>
                  </div>
                )}
                {booking.offer?.id && !booking.notes && (
                  <div className="DiarySidebarListItem__customerRequestsRow">
                    <img src={Quote} alt="Quote" />
                  </div>
                )}

                {/* Deposits - Payment Total */}
                {getBookingPaymentTotal(booking) > 0 && (
                  <div className="DiarySidebarListItem__customerRequestsRow">
                    <CustomPill
                      label={`£${getBookingPaymentTotal(booking).toFixed(2)} paid`}
                      backgroundColour="#6b7280"
                      labelColour="#ffffff"
                    />
                  </div>
                )}

                {/* Deposits - Auth Total */}
                {getBookingAuthTotal(booking) > 0 && (
                  <div className="DiarySidebarListItem__customerRequestsRow">
                    <CustomPill
                      label={`£${getBookingAuthTotal(booking).toFixed(2)} authenticated`}
                      backgroundColour="#6b7280"
                      labelColour="#ffffff"
                    />
                  </div>
                )}

                {/* Preorders counter */}
                {getActivePreorderCount(booking) > 0 && (
                  <div className="DiarySidebarListItem__customerRequestsRow">
                    <CustomPill
                      label={`${getActivePreorderCount(booking)} package${getActivePreorderCount(booking) > 1 ? "s" : ""}`}
                      backgroundColour="#6b7280"
                      labelColour="#ffffff"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Column 6 - Customer Notes */}
            <div className="Column6">
              {(booking.comments?.length ?? 0) !== 0 && (
                <>
                  <div className="DiarySidebarListItem__comments">
                    {booking.comments.length > 0 &&
                      booking.comments.map((comment: any, index: number) => (
                        <span
                          key={index}
                          className="DiarySidebarListItem__comment"
                        >
                          {comment.comment}
                        </span>
                      ))}
                  </div>
                </>
              )}
            </div>

            {/* Column 7 - Actions*/}
            <ActionsColumn
              booking={booking}
              onSwipeoutOptionClick={swipeoutOptionClick}
              onShowPreviewModal={() => setShowPreviewModal(true)}
              onEditBooking={() => {
                diaryContext.setDiaryContext({
                  editBookingId: booking.id
                });
              }}
            />
          </div>
        </EDSSwipeout>
        <button
          type="button"
          className="DragIconContainer"
          style={{
            width: selectedView === "FloorplanTab" ? "36px" : "0"
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onDragStart={(e: React.DragEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            e.dataTransfer.setData("booking", JSON.stringify(booking));

            const clone = createDragClone(booking.id);
            if (clone) {
              const offset = isTouchDevice
                ? calculateTouchDragOffset(clone)
                : calculateDesktopDragOffset(clone);
              e.dataTransfer.setDragImage(clone, offset, 20);
            }
          }}
          onDragEnd={() => {
            cleanupDragClones();
          }}
          draggable={true}
          data-drag="true"
          aria-label="Drag booking to floorplan"
        >
          <GripVertical
            size={28}
            className="BookingItem-Icon"
            strokeWidth={2}
          />
        </button>
      </motion.div>
    </>
  );
};

type DisplayMoreIconTProps = {
  bookingId: string;
};
const DisplayMoreIcon = ({ bookingId }: DisplayMoreIconTProps) => {
  const bookingCardDiv = document.getElementById(
    `bookingCard_${bookingId}`
  ) as HTMLElement | null;
  return (
    <>
      {bookingCardDiv &&
      bookingCardDiv?.scrollWidth > bookingCardDiv?.clientWidth ? (
        <div className="MoreIcon">
          <img src={More} alt="more" />
        </div>
      ) : null}
    </>
  );
};

/**
 * Helper function to determine if booking labels section should be shown
 * Extracted to reduce cognitive complexity of RunSheetCard component
 */
const shouldShowBookingLabels = (booking: ViewBooking): boolean => {
  return Boolean(
    booking.labels ||
    booking.customer_labels ||
    booking.doubleConfirmed ||
    booking.linkedBookings ||
    booking.hasPreorders ||
    booking.deposits ||
    booking.company
  );
};

/**
 * ActionsColumn component - extracted to reduce cognitive complexity
 * Renders the action buttons for a booking card based on current stage
 */
type ActionsColumnProps = {
  booking: ViewBooking;
  onSwipeoutOptionClick: (name: string, booking: ViewBooking) => void;
  onShowPreviewModal: () => void;
  onEditBooking: () => void;
};

const ActionsColumn = ({
  booking,
  onSwipeoutOptionClick,
  onShowPreviewModal,
  onEditBooking
}: ActionsColumnProps) => {
  const isArrived = booking.currentStage === "arrived";

  if (isArrived) {
    return (
      <div className="Column7">
        <div className="RunsheetCardListRow">
          <div className="DiarySidebarListItem__actions">
            <button
              className="DiarySidebarListItem__action DiarySidebarListItem__action--left"
              onClick={(e) => {
                e.stopPropagation();
                onSwipeoutOptionClick("left", booking);
              }}
            >
              Left
            </button>
            <button
              className="DiarySidebarListItem__action DiarySidebarListItem__action--details"
              onClick={(e) => {
                e.stopPropagation();
                onShowPreviewModal();
              }}
              title="Show booking details"
            >
              <img src={Eye} alt="View Booking" />
            </button>
            <button
              className="DiarySidebarListItem__action DiarySidebarListItem__action--edit"
              onClick={(e) => {
                e.stopPropagation();
                onEditBooking();
              }}
              title="Edit booking"
            >
              <img src={ExternalLink} alt="Edit Booking" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="Column7">
      <div className="RunsheetCardListRow">
        <div className="DiarySidebarListItem__actions">
          <button
            className="DiarySidebarListItem__action DiarySidebarListItem__action--checkIn"
            onClick={(e) => {
              e.stopPropagation();
              onSwipeoutOptionClick("arrived", booking);
            }}
          >
            Check In
          </button>
          <button
            className="DiarySidebarListItem__action DiarySidebarListItem__action--noShow"
            onClick={(e) => {
              e.stopPropagation();
              onSwipeoutOptionClick("no-show", booking);
            }}
          >
            No Show
          </button>
          <button
            className="DiarySidebarListItem__action DiarySidebarListItem__action--details"
            onClick={(e) => {
              e.stopPropagation();
              onShowPreviewModal();
            }}
            title="Show booking details"
          >
            <img src={Eye} alt="View Booking" />
          </button>
          <button
            className="DiarySidebarListItem__action DiarySidebarListItem__action--edit"
            onClick={(e) => {
              e.stopPropagation();
              onEditBooking();
            }}
            title="Edit booking"
          >
            <img src={ExternalLink} alt="Edit Booking" />
          </button>
        </div>
      </div>
    </div>
  );
};
